import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { EasCard } from 'ui/EasCard';
import { PAGE_SIZE } from 'services/constants/env';
import { ESortDirection, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { TFilters, TOrderBy } from 'redux/types';
import { getEMerchantRequest } from 'redux/reducers/monthlyFee/reducer';
import { getEmEnumsRequest } from 'redux/reducers/emManagement/reducer';
import { EEmEnums } from 'redux/reducers/emManagement/constants';
import {
	clearPrpPostalOperatorSettings,
	clearPrpSettings,
	getPrpPostalOperatorSettingsRequest,
	getPrpSettingsRequest,
} from 'redux/reducers/prp/reducer';
import {
	getPrpPostalOperatorSettingsList,
	getPrpPostalOperatorSettingsListLoading,
	getPrpSettings,
	getPrpSettingsLoading,
} from 'redux/reducers/prp/selectors';
import { TPrpPostalOperatorSettingsItem } from 'redux/reducers/prp/types';
import { EDIT_ACTION, tableStructure as defaultTableStructure } from './tableStructure';
import { PrpSettingsDialog } from './PrpSettingsDialog';
import { PostalOperatorSettingsDialog } from './PostalOperatorSettingsDialog';
import { EmFilter } from '../../ui/EasTableFilterPanel/EmFilter';
import { COLORS } from 'services/constants/colors';

export const Prp: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const emId = navigateParams.searchEmId ? Number(navigateParams.searchEmId) : null;

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
	const [rowToEdit, setRowToEdit] = useState<TPrpPostalOperatorSettingsItem | null>(null);

	const settings = useSelector(getPrpSettings);
	const settingsLoading = useSelector(getPrpSettingsLoading);
	const list = useSelector(getPrpPostalOperatorSettingsList);
	const listLoading = useSelector(getPrpPostalOperatorSettingsListLoading);

	useEffect(() => {
		dispatch(getEMerchantRequest());
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.SUPPORTED_CURRENCY }));
	}, [dispatch]);

	const structure = useMemo(
		() =>
			currentTableStructure.map((column) =>
				column.type === ETableStructureType.AMOUNT && !column.currency
					? {
							...column,
							currency: (item: TPrpPostalOperatorSettingsItem) =>
								item.handlingFeeCurrencyIsoCode ?? '',
						}
					: column,
			),
		[currentTableStructure],
	);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TPrpPostalOperatorSettingsItem>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	const loadList = useCallback(() => {
		if (!emId) return;
		const params = queryString.parse(search);
		const filters = currentTableStructure.reduce((acc, item) => {
			if (item.filterable && params[item.selector as string]) {
				acc.push({
					fieldName: item.selector as string,
					value: params[item.selector as string] as string,
					operator: '=',
				});
			}
			return acc;
		}, [] as TFilters);

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(getPrpSettingsRequest({ emId }));
		dispatch(
			getPrpPostalOperatorSettingsRequest({
				emId,
				body: {
					pageNumber: params.page ? +params.page : 1,
					pageSize: PAGE_SIZE,
					searchString: String(params?.search ?? ''),
					filters,
					orderBy,
				},
			}),
		);
	}, [emId, currentTableStructure, dispatch, search]);

	useEffect(() => {
		if (emId) {
			loadList();
		} else {
			dispatch(clearPrpSettings());
			dispatch(clearPrpPostalOperatorSettings());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [emId, loadList]);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action === EDIT_ACTION) {
				setRowToEdit(list?.data?.find((item) => item.id === id) ?? null);
			}
		},
		[list],
	);

	const handleOpenSettingsDialog = useCallback(() => {
		setSettingsDialogOpen(true);
	}, []);

	const settingsDialogMode = settings?.prpEnabled ? 'edit' : 'activate';

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', m: 2 }}>
				<Typography variant="h5" fontWeight="bold" sx={{ color: COLORS.PRIMARY }}>
					PRP
				</Typography>
				<EmFilter />
			</Box>
			{!!emId && (
				<>
					<EasCard
						id="prp-settings"
						title="PRP Settings"
						loading={settingsLoading}
						action={
							<Button
								variant="contained"
								size="small"
								onClick={handleOpenSettingsDialog}
								disabled={settingsLoading}
							>
								{settings?.prpEnabled ? 'Edit Settings' : 'Activate PRP'}
							</Button>
						}
					>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, p: 1.5, alignItems: 'center' }}>
							<Chip
								label={settings?.prpEnabled ? 'Enabled' : 'Disabled'}
								color={settings?.prpEnabled ? 'success' : 'default'}
								size="small"
							/>
							<Typography variant="body2">
								Representation Fee: <b>{settings?.representationFee ?? 0}</b>
							</Typography>
							<Typography variant="body2">
								PDDP API Key: <b>{settings?.hasPddpApiKey ? 'Set' : 'Not set'}</b>
							</Typography>
						</Box>
					</EasCard>
				</>
			)}

			{!!emId && (
				<EasTableWithFilter<TPrpPostalOperatorSettingsItem>
					tableStructure={structure}
					items={list}
					itemsIsLoading={listLoading}
					handleAction={handleAction}
					onTableStructureChange={handleTableStructureChange}
				>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<Typography variant="body1" color="primary" fontWeight="bold">
							PRP — Postal Operator Settings
						</Typography>
					</Box>
				</EasTableWithFilter>
			)}

			{emId && (
				<>
					<PrpSettingsDialog
						open={settingsDialogOpen}
						onClose={() => setSettingsDialogOpen(false)}
						emId={emId}
						mode={settingsDialogMode}
						settings={settings}
					/>
					<PostalOperatorSettingsDialog
						open={!!rowToEdit}
						onClose={() => setRowToEdit(null)}
						emId={emId}
						item={rowToEdit}
					/>
				</>
			)}
			{!emId && (
				<Typography
					variant="body2"
					sx={{
						textAlign: 'center',
						mt: 20,
						color: COLORS.SECONDARY,
						fontWeight: 'bold',
						fontSize: '2.2rem',
					}}
				>
					Search and select an EM above to view its PRP settings
				</Typography>
			)}
		</Box>
	);
};
